import { getOS } from '@/shared/tools/get-os';
import { Capacitor } from '@capacitor/core';
import { RouteLocationNormalized } from 'vue-router';
import { logAnalyticsEvent } from '@/core/analytics/analytics';
import { AnalyticsEventName, SectionEntrySourceNames } from '@/core/analytics/types';
import { getSectionNameFromPath } from '@/shared/tools/get-screen-from-url';
import { useInviteStore } from '@/modules/invite/stores/invite';
import { resolveContentPathFromRoute } from '@/modules/invite/utils/content-path';

// Listens when a shared link opens our site / app.
// Persists invite params, records clicks, and redirects mobile web to ChottuLink.

export const useShareLinkDetector = () => {
  const shareLinkOpenedSaveData = (url: string) => {
    if (!url) {
      return;
    }

    const urlObject = new URL(url);

    const sectionName = getSectionNameFromPath(urlObject.pathname);

    logAnalyticsEvent({
      name: AnalyticsEventName.SectionEntry,
      params: {
        section_name: sectionName,
        source: SectionEntrySourceNames.DeepLink,
      },
    });
  };

  const buildChottuLinkRedirectUrl = (route: RouteLocationNormalized): string | null => {
    try {
      const subdomain = 'https://ohmybible.chottu.link';
      const params = new URLSearchParams();

      const inviteId = route.query.inviteId as string | undefined;
      const contentPath = resolveContentPathFromRoute(route);
      const shareId = route.query.shareId as string | undefined;

      if (inviteId) {
        params.set('inviteId', inviteId);
      }

      if (contentPath) {
        params.set('contentPath', contentPath);
      }

      if (shareId) {
        params.set('shareId', shareId);
      }

      params.set('_ch_incp', '1');

      return `${subdomain}/invite?${params.toString()}`;
    } catch (error) {
      console.error('Failed to build ChottuLink redirect URL', error);

      return null;
    }
  };

  const redirectIfShareLinkOnMobile = (route: RouteLocationNormalized) => {
    const shareId = route?.query?.shareId as string | undefined;
    const inviteId = route?.query?.inviteId as string | undefined;

    if ((!shareId && !inviteId) || Capacitor.isNativePlatform()) {
      return;
    }

    const userOS = getOS();
    const isMobileOs = userOS === 'iOS' || userOS === 'Android';

    if (!isMobileOs) {
      return;
    }

    const inviteStore = useInviteStore();
    const currentUrl = `${window.location.origin}${route.fullPath}`;
    inviteStore.persistInviteFromUrl(currentUrl);

    const chottuUrl = buildChottuLinkRedirectUrl(route);

    if (chottuUrl) {
      window.location.href = chottuUrl;

      return false;
    }
  };

  return {
    shareLinkOpenedSaveData,
    redirectIfShareLinkOnMobile,
  };
};
